import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    OnInit
} from '@angular/core';
import {
    BackgroundColorMap,
    defaultPaletteColor
} from '../../constants-types/ColorPalettePicker.model';
import { Person } from '../../core-data-model/Person.model';
import { TranslationService } from '../../translations/Translation.service';

@Component({
    selector: 'ok-pill',
    template: `
        <div
            class="ok-pill"
            [ngStyle]="{
                margin: isColorPickerEnabled
                    ? '0 10px 10px 0px'
                    : '0 5px 10px 5px;',
                opacity: isDisabled ? 0.4 : 1
            }"
            tooltip
            [tooltipContent]="disabledState"
            [tooltipAnimation]="true"
            [tooltipDisabled]="!isDisabled"
            tooltipPlacement="bottom"
        >
            <span
                style="display: flex; align-items: center;"
                [ngClass]="[
                    isPillValid(),
                    getColorPickerOverride(),
                    getPillSelection()
                ]"
                [ngStyle]="{
                    'border-radius':
                        isColorPickerEnabled && item.visible
                            ? '4px 0px 0px 4px'
                            : '4px',
                    padding: isColorPickerEnabled ? '2px 7px' : '0px'
                }"
            >
                <div class="ok-pill__content" piTesting="ok-pill-content">
                    <ng-template
                        [ngTemplateOutlet]="contentTemplate"
                        [ngTemplateOutletContext]="{
                            item: item
                        }"
                    >
                    </ng-template>
                </div>
                @if (editable) {
                    <div
                        class="ok-pill__control ok-pill__edit"
                        (click)="onEdit.emit()"
                    >
                        <i
                            class="fa prototype-grey"
                            [ngClass]="{
                                'fa-pencil': isValid,
                                'fa-exclamation-triangle': !isValid
                            }"
                            aria-hidden="true"
                        >
                        </i>
                    </div>
                }
                @if (removable === true || removable === null) {
                    <div
                        class="ok-pill__control ok-pill__delete"
                        (click)="onRemove.emit()"
                        [ngStyle]="{
                            'pointer-events': isDisabled ? 'none' : 'auto'
                        }"
                        piTesting="ok-pill-remove"
                    >
                        x
                    </div>
                }
            </span>
            <ng-content select="[tooltip]"></ng-content>
            @if (isColorPickerEnabled) {
                <div>
                    <span
                        [ngClass]="
                            colorPickerOverride
                                ? colorPickerOverride +
                                  backgroundColorOpacityPostfix
                                : defaultBackgroundColor +
                                  backgroundColorOpacityPostfix
                        "
                        class="dotsContainer"
                    >
                        <svg
                            width="25"
                            height="17"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g
                                transform="translate(.977 .627)"
                                fill="#3D1C70"
                                fill-rule="evenodd"
                            >
                                <circle cx="1.771" cy="1.771" r="1.771" />
                                <circle cx="1.771" cy="7.97" r="1.771" />
                                <circle cx="1.771" cy="14.169" r="1.771" />
                            </g>
                        </svg>
                    </span>
                    @if (isColorPickerEnabled) {
                        <color-palette-picker
                            [person]="item.personObject"
                            [index]="index"
                            (selectedColor)="emittedColor($event, item)"
                            [showPicker]="showPicker"
                            (pickerToggled)="pickerToggled.emit($event)"
                        ></color-palette-picker>
                    }
                </div>
            }
        </div>
        <tooltip-content #disabledState>
            {{ TR.key('teamDiscovery-tooltip-wait') }}
        </tooltip-content>
    `,
    styles: [
        `
            .dotsContainer {
                width: 25px;
                border-radius: 0px 4px 4px 0px;
                cursor: pointer;
            }
        `
    ],
    standalone: false
})
export class OkPillComponent implements OnInit {
    @ContentChild(TemplateRef, { static: false })
    contentTemplate: TemplateRef<any>;

    @Input()
    isPillSelected = '';
    @Input()
    isColorPickerEnabled = false;
    @Input()
    item: any;
    @Input()
    index: number;
    @Input()
    isValid = true;
    @Input()
    editable = false;
    @Input()
    removable = false;
    @Input()
    showPicker = false;
    @Input()
    currentId: string;
    @Input()
    tooltipEnabled = false;
    @Input()
    isDisabled = false;
    @Output()
    readonly onRemove = new EventEmitter<any>();
    @Output()
    readonly onEdit = new EventEmitter<any>();
    @Output()
    onColorChange = new EventEmitter<void>();
    @Output()
    pickerToggled = new EventEmitter<number>();

    colorPickerOverride = '';
    backgroundColorOpacityPostfix = '-opacity';
    defaultBackgroundColor = '';

    constructor(public TR: TranslationService) {}

    ngOnInit() {
        if (this.item instanceof Person) {
            this.colorPickerOverride =
                BackgroundColorMap[this.item.displayColor];
        } else if (
            Object.prototype.hasOwnProperty.call(this.item, 'selfPattern')
        ) {
            this.colorPickerOverride =
                BackgroundColorMap[this.item.personObject.displayColor];
        }
        // Set bg-color-person as default for pizza person pills and set bg-color-primary-900 for all other UI pages
        this.defaultBackgroundColor = this.isColorPickerEnabled
            ? BackgroundColorMap[defaultPaletteColor]
            : 'ok-default-pill-background';
    }

    getColorPickerOverride() {
        // This is a workaround for person pill in the person page
        // We're not setting any background color for ok-pill--notselected else it won't apply the white background
        if (this.getPillSelection() === 'ok-pill--notselected') {
            return '';
        } else {
            return this.colorPickerOverride
                ? this.colorPickerOverride
                : this.defaultBackgroundColor;
        }
    }

    getPillSelection() {
        return this.isPillSelected;
    }

    isPillValid() {
        return this.isValid ? '' : 'ok-pill--invalid';
    }

    emittedColor(color: number, person: Person) {
        this.colorPickerOverride = BackgroundColorMap[color];
        // This is bound to pizzaCrust person teardrop color
        person.displayColor = color;
        this.onColorChange.emit();
    }
}
